import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import shopWebsiteService, { type ShopWebsiteConfig } from '@/services/shopWebsiteService'
import { shopDesignTokenCssVars } from '@/lib/shopDesignTokens'
import { resolveShopSlugFromAuthLocation } from '@/lib/shopAuthSlug'

export type ShopAuthBranding = {
  loading: boolean
  slug: string | null
  config: ShopWebsiteConfig | null
  primary: string
  /** Query suffix like `?shop=demostore` for auth links, or empty. */
  shopQuery: string
  tokenStyle: Record<string, string>
}

const DEFAULT_PRIMARY = '#0f766e'

export { resolveShopSlugFromAuthLocation } from '@/lib/shopAuthSlug'

/**
 * Loads storefront website config when customer auth is opened from a shop
 * (`?shop=slug` or return path `/shop/:slug`).
 */
export function useShopAuthBranding(): ShopAuthBranding {
  const location = useLocation()
  const slug = useMemo(() => resolveShopSlugFromAuthLocation(location), [location])
  const [loading, setLoading] = useState(Boolean(slug))
  const [config, setConfig] = useState<ShopWebsiteConfig | null>(null)

  useEffect(() => {
    if (!slug) {
      setConfig(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const res = await shopWebsiteService.getBySlug(slug)
        if (!cancelled && res.success && res.data) {
          setConfig(res.data)
        } else if (!cancelled) {
          setConfig(null)
        }
      } catch {
        if (!cancelled) setConfig(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [slug])

  const primary = (config?.primary_color || '').trim() || DEFAULT_PRIMARY
  const tokenStyle = useMemo(() => {
    if (!config) return {}
    return shopDesignTokenCssVars({
      primaryColor: config.primary_color,
      secondaryColor: config.secondary_color,
      fontFamily: config.font_family,
      designTokens: config.design_tokens || undefined,
    }) as Record<string, string>
  }, [config])

  return {
    loading,
    slug,
    config,
    primary,
    shopQuery: slug ? `?shop=${encodeURIComponent(slug)}` : '',
    tokenStyle,
  }
}
